import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
    open: boolean;
    title: string;
    description?: string;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg';
}

const SIZES = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
} as const;

export function Modal({
    open,
    title,
    description,
    onClose,
    children,
    footer,
    size = 'md',
}: ModalProps) {
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', onKeyDown);

        // Le défilement de l'arrière-plan pendant qu'une modale est ouverte est
        // désorientant, surtout sur mobile.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Le focus entre dans la modale : sans cela, la navigation au clavier
        // resterait derrière l'écran assombri.
        dialogRef.current?.focus();

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn"
            role="presentation"
        >
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
                onClick={onClose}
                aria-hidden
            />

            <div
                ref={dialogRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                aria-describedby={description ? 'modal-description' : undefined}
                className={`relative w-full ${SIZES[size]} card animate-slideUp max-h-[90vh] flex flex-col outline-none rounded-b-none sm:rounded-xl`}
            >
                <header className="flex items-start justify-between gap-4 p-5 border-b border-[var(--color-border)]">
                    <div>
                        <h2 id="modal-title" className="text-lg font-semibold text-[var(--color-text)]">
                            {title}
                        </h2>
                        {description && (
                            <p
                                id="modal-description"
                                className="mt-1 text-sm text-[var(--color-text-secondary)]"
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-ghost p-1.5"
                        aria-label="Fermer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                <div className="p-5 overflow-y-auto flex-1">{children}</div>

                {footer && (
                    <footer className="flex items-center justify-end gap-2 p-5 border-t border-[var(--color-border)]">
                        {footer}
                    </footer>
                )}
            </div>
        </div>
    );
}
